import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '../ui/card';
import { ProductCard } from './ProductCard';
import { ProductListItem } from './ProductListItem';
import { Product } from '../../services/product';
import { ChevronLeft, ChevronRight, Package, Loader2 } from 'lucide-react';

interface ProductListProps {
  products: Product[];
  loading: boolean;
  viewMode: 'grid' | 'list';
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  onPageChange: (page: number) => void;
}

export function ProductList({ products, loading, viewMode, pagination, onPageChange }: ProductListProps) {
  // Show skeleton loader during loading (initial or refresh)
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm font-medium text-muted-foreground">Loading products...</span>
          </div>
        </div>

        {/* Skeleton grid/list */}
        <div className="animate-pulse">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="h-80 bg-muted rounded-xl"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="h-56 bg-muted rounded-xl"></div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Only show "No Products Found" when NOT loading and products array is empty
  if (products.length === 0) {
    return (
      <Card className="card-modern">
        <CardContent className="flex flex-col items-center justify-center py-16">
          <div className="p-4 bg-muted/50 rounded-full mb-6">
            <Package className="h-12 w-12 text-muted-foreground" />
          </div>
          <CardTitle className="text-xl mb-3">No Products Found</CardTitle>
          <CardDescription className="text-center mb-8 max-w-md">
            No products match your current filters. Try adjusting your search criteria or sync products from your stores.
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="space-y-8">
      {/* Results summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-muted-foreground">
          Showing <span className="text-foreground">{startItem}-{endItem}</span> of <span className="text-foreground">{pagination.total}</span> products
        </div>
        {loading && (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Updating...
          </div>
        )}
      </div>

      {/* Products display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <div 
              key={product.id} 
              className="animate-slide-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {products.map((product, index) => (
            <div 
              key={product.id} 
              className="animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <ProductListItem product={product} />
            </div>
          ))}
        </div>
      )}

      {/* Modern Pagination */}
      {pagination.pages > 1 && (
        <Card className="card-modern">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page <span className="font-medium text-foreground">{pagination.page}</span> of <span className="font-medium text-foreground">{pagination.pages}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? "default" : "outline"}
                        size="sm"
                        onClick={() => onPageChange(pageNum)}
                        disabled={loading}
                        className="w-10 h-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages || loading}
                  className="gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}